import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse, { attributesToProps, domToReact, HTMLReactParserOptions } from "html-react-parser";
import React, { useContext } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    topSectionRichContent = "topSectionRichContent",
    topSectionExpertImage = "topSectionExpertImage",
    experienceTitle = "experienceTitle",
    mobileNumber = "mobileNumber",
    timeSlot = "timeSlot",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.topSectionRichContent]: string;
    };
}

export interface CENBrandDetailsInfotyles {
    sectionContainer?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    richContentSection?: TypographyPresentationProps;
}

export const CENBrandDetailsInfotyles: CENBrandDetailsInfotyles = {
    sectionContainer: {
        css: css`
            height: 100%;
            margin: 40px 20px;
            @media all and (max-width: 767px){
                margin: 24px 0;
            }
        `
    },
    sectionTitle: {
        css: css`
            font-weight: 600;
            font-size: 24px;
            line-height: 32px;
            color: #262525;
            display: block;
            margin-bottom: 20px;
        `
    },
    richContentSection: {
        css: css`
            & h2, & h3, & h4 {
                font-weight: 600;
                font-size: 18px;
                line-height: 24px;
                color: #262525;
            }
            & p {
                font-size: 18px;
                color: #262525;
                line-height: 28px;
            }
        `
    },
};


type Props = OwnProps

const styles = CENBrandDetailsInfotyles;


// Extend the shared parserOptions so tables get wrapped in a scrollable div,
// without breaking any existing replace() logic from parserOptions.
const richContentParserOptions: HTMLReactParserOptions = {
    ...parserOptions,
    replace: (domNode, index) => {
        // Preserve any existing replace behavior first.
        if (typeof (parserOptions as HTMLReactParserOptions | undefined)?.replace === "function") {
            const existingResult = (parserOptions as HTMLReactParserOptions).replace!(domNode, index);
            if (existingResult) {
                return existingResult;
            }
        }

        if (domNode.type === "tag" && domNode.name === "table") {
            const tableProps = attributesToProps(domNode.attribs);
            return (
                <div style={{ width: "100%", overflowX: "auto" }}>
                    <table {...tableProps} style={{ borderCollapse: "collapse", minWidth: "700px", width: "100%" }}>
                        {domToReact(domNode.children as any, richContentParserOptions)}
                    </table>
                </div>
            );
        }

        return undefined;
    },
};


const CENBrandDetailsInfo: React.FC<Props> = ({ fields }) => {
    const { value: brand } = useContext(BrandStateContext);

    if (!brand) return null

    return (
        <StyledWrapper {...styles.sectionContainer} data-test-selector="brandDetailsInfoSection" id="brand-details-information">
            <StyledWrapper>
                <StyledWrapper as="h2" {...styles.sectionTitle}>
                    About {brand.name}
                </StyledWrapper>
                <StyledWrapper as="div" {...styles.richContentSection}>{parse(transformContentHtml(brand.htmlContent), richContentParserOptions)}</StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENBrandDetailsInfo,
    definition: {
        group: "Common",
        displayName: "CEN Brand Details Full",
    },
};

export default widgetModule;